import { pickEditableOrderDetail, pickEditableCourier, pickDraftTemplateImages } from "./completeOrderDraftFields";

const STORAGE_KEY = "completeOrderDraft";
const CHANGE_EVENT = "complete-order-draft-change";

function notifyChange() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }
}

function getCurrentUserId() {
  try {
    return localStorage.getItem("userId");
  } catch (e) {
    return null;
  }
}

function getUserStorageKey(userId) {
  return `${STORAGE_KEY}:${userId}`;
}

/**
 * 将旧版全局 key 草稿迁移到 userId 分桶（只迁移一次）
 */
function migrateLegacyDraftIfNeeded(userId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return;
    }
    const draft = JSON.parse(raw);
    localStorage.removeItem(STORAGE_KEY);
    if (!draft || !draft.orderId) {
      return;
    }
    // 旧数据无 userId：归属当前登录用户；有 userId 则迁到对应用户桶
    const ownerId =
      draft.userId != null && draft.userId !== ""
        ? String(draft.userId)
        : String(userId);
    draft.userId = ownerId;
    const key = getUserStorageKey(ownerId);
    if (!localStorage.getItem(key)) {
      localStorage.setItem(key, JSON.stringify(draft));
    }
  } catch (e) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      // ignore
    }
  }
}

function pickLightDraft(payload, userId) {
  return {
    userId,
    orderId: payload.orderId,
    goBackUrl: payload.goBackUrl,
    goBackType: payload.goBackType,
    orderDetail: payload.orderDetail,
    orderCourier: payload.orderCourier,
    goBackOrderType: payload.goBackOrderType,
    savedAt: Date.now(),
  };
}

export function getCompleteOrderDraft() {
  const userId = getCurrentUserId();
  if (!userId) {
    return null;
  }
  try {
    migrateLegacyDraftIfNeeded(userId);
    const raw = localStorage.getItem(getUserStorageKey(userId));
    if (!raw) {
      return null;
    }
    const draft = JSON.parse(raw);
    if (!draft || !draft.orderId) {
      return null;
    }
    if (String(draft.userId) !== String(userId)) {
      return null;
    }
    return draft;
  } catch (e) {
    return null;
  }
}

export function hasCompleteOrderDraft() {
  const draft = getCompleteOrderDraft();
  return !!(draft && draft.orderId);
}

export function saveCompleteOrderDraft(payload) {
  if (!payload || !payload.orderId) {
    return false;
  }
  const userId = payload.userId || getCurrentUserId();
  if (!userId) {
    return false;
  }
  const storageKey = getUserStorageKey(userId);
  try {
    const data = {
      userId: String(userId),
      orderId: payload.orderId,
      goBackUrl: payload.goBackUrl,
      goBackType: payload.goBackType,
      goBackOrderType: payload.goBackOrderType,
      orderDetail: pickEditableOrderDetail(payload.orderDetail),
      orderCourier: pickEditableCourier(payload.orderCourier),
      imageSetting: pickDraftTemplateImages(payload.imageSetting),
      orderImage: payload.orderImage,
      savedAt: Date.now(),
    };
    // 去掉 File/content 等无法序列化或过大的字段，避免 localStorage 写入失败
    const text = JSON.stringify(data, (key, value) => {
      if (key === "file" || key === "content" || key === "objectUrl") {
        return undefined;
      }
      return value;
    });
    localStorage.setItem(storageKey, text);
    notifyChange();
    return true;
  } catch (e) {
    console.warn("saveCompleteOrderDraft failed", e);
    try {
      // 空间不足时再尝试只保留核心表单字段
      localStorage.setItem(
        storageKey,
        JSON.stringify(pickLightDraft({
          ...payload,
          orderDetail: pickEditableOrderDetail(payload.orderDetail),
          orderCourier: pickEditableCourier(payload.orderCourier),
        }, String(userId)))
      );
      notifyChange();
      return true;
    } catch (err) {
      console.warn("saveCompleteOrderDraft light failed", err);
      return false;
    }
  }
}

export function clearCompleteOrderDraft() {
  const userId = getCurrentUserId();
  if (userId) {
    localStorage.removeItem(getUserStorageKey(userId));
  }
  // 兼容清理旧版全局 key
  localStorage.removeItem(STORAGE_KEY);
  notifyChange();
}

export function onCompleteOrderDraftChange(handler) {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export { STORAGE_KEY, CHANGE_EVENT };
